import { Locale } from '@finxone-platform/shared/enums';
import { NotificationDeviceOS, NotificationMark, NotificationMarkChannelEnum } from './common.enum';
import { NotificationWorkflowSetting } from './common.type';

export type NotificationProviderInfoType = {
  provider: string;
};
export type NotificationList = {
  title: string;
  description: string;
};

export type NotificationListRequest = {
  page: number;
  userId: string;
  pageSize: number;
  organisationId: string;
};

export type NotificationSubscriberUserInfo = {
  id: string;
  orgIds: string[];
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  deviceTokens: string[];
  deviceType?: NotificationDeviceOS;
};

export type MarkAllNotificationInfoRequest = {
  userId: string;
  orgId: string;
  feedIdentifierIds?: string[];
  markAs: NotificationMark;
  channel?: NotificationMarkChannelEnum;
};

export type NotificationsStatusRequest = {
  markAs: NotificationMark;
  channel: NotificationMarkChannelEnum;
};

export type NotificationFeedRequest = {
  userId: string;
  orgId: string;
  seen?: boolean;
  read?: boolean;
  channel?: NotificationMarkChannelEnum;
  page?: number;
  size?: number;
};

export type NotificationTriggerInfoRequest = {
  subscriber?: NotificationSubscriberUserInfo;
  notificationPayload: NotificationTriggerRequest;
  tenant?: string;
};

export type SendTestNotificationRequest = {
  workflowName: string;
  to: {
    email?: string;
    phone?: string;
  };
  payload: NotificationPayload;
  tenant?: string;
};

export type NotificationTriggerRequest = {
  subscriberId: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: NotificationPayload;
  template: NotificationTemplateEnum;
  locale?: Locale;
  workflowSetting?: NotificationWorkflowSetting;
  uniqueKey?: string;
};

export type NotificationPayloadEmailSettings = {
  cc?: string[];
  to?: string[];
  from?: string;
  senderName?: string;
  text?: string;
  replyTo?: string;
  bcc?: string[];
};

export type Credentials = {
  deviceTokens: string[];
};

export type SubscriberData = {
  _integrationId: string;
  providerId: string;
  credentials: Credentials;
};

export enum NotificationTransactionStatus {
  SUCCESS = 'success',
  FAIL = 'failed',
  CANCELLED = 'cancelled',
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum NotificationTypeEnum {
  TRANSACTION_INCOMING = 'transaction-incoming',
  TRANSACTION_OUTGOING = 'transaction-outgoing',
  DAILY_BALANCE = 'daily-balance',
  EXCHANGE_COMPLETED = 'exchange-completed',
  EXCHANGE_FAILED = 'exchange-failed',
  PAYMENT_REQUEST = 'payment-request',
  PAYMENT_REQUEST_APPROVED = 'request-approved',
  PAYMENT_REQUEST_REJECTED = 'request-rejected',
  BENEFICIARY_ADDED = 'beneficiary-added',
  BENEFICIARY_UPDATED = 'beneficiary-updated',
  BENEFICIARY_REMOVED = 'beneficiary-removed',
  BULK_PAYMENT_REQUEST = 'bulk-payment-request',
  BULK_PAYMENT_REQUEST_APPROVAL = 'bulk-payment-request-approval',
  KYC_APPLICATION_ACCEPTED = 'kyc-application-accepted',
  GENERIC_WORKFLOW = 'generic-workflow',
  FAILURE_ALERT = 'failure-alert',
  INCOMPLETE_ONBOARDING = 'incomplete-onboarding',
  APPLICATION_REJECTION = 'application-rejection',
  THREEDS_CHALLENGE = '3ds-challenge',
  CARD_CREATED = 'card-created',
  PHYSICAL_CARD_ACTIVATED = 'physical-card-activated',
  DIGITAL_WALLET_AVAILABLE = 'digital-wallet-available',
  DIGITAL_WALLET_ACTIVATED = 'digital-wallet-activated',
  DIGITAL_WALLET_ABANDONED_PROVISIONING = 'digital-wallet-abandoned-provisioning',
  DIGITAL_WALLET_PAYMENT_REMINDER = 'digital-wallet-payment-reminder',
  DIGITAL_WALLET_PROVISIONING_OTP = 'digital-wallet-provisioning-otp',
  KYC_RFI_ACTION_REQUIRED = 'kyc-rfi-action-required',
  JOINT_ACCOUNT_INVITATION = 'joint-account-invitation',
  JOINT_ACCOUNT_INVITATION_ACTION = 'joint-account-invitation-action',
  JOINT_ACCOUNT_INVITATION_EXPIRED = 'joint-account-invitation-expired',
  DEVICE_ENROLLED = 'device-enrolled',
  DEVICE_REMOVED = 'device-removed',
  CUSTOMER_DOCUMENT_UPLOAD_CONFIRMATION = 'customer-document-upload-confirmation',
  // Sterling credits (stage C3 of the token-credits plan, §6). Fired once per calendar month
  // per threshold: an org sitting below its low-balance line crosses it on every turn, and
  // forty identical notices is how a useful one becomes one people filter out.
  CREDITS_LOW_BALANCE = 'credits-low-balance',
  CREDITS_MEMBER_LIMIT = 'credits-member-limit',
  // Sent once a VRP mandate is fulfilled (active) with the payer's bank - GC's 1.1 setup
  // confirmation requirement (9920).
  VRP_MANDATE_SETUP_CONFIRMATION = 'vrp-mandate-setup-confirmation',
  // Sent ~48h before a scheduled VRP collection is charged - GC's 1.3 advance notice
  // requirement (9920).
  VRP_MANDATE_ADVANCE_NOTICE = 'vrp-mandate-advance-notice',
  // Proactive AI companion nudge (docs/platform/ai-companion/internal-api.md, "Proactive nudges").
  COMPANION_NUDGE = 'companion-nudge',
  NOTICE_WITHDRAWAL_SUBMITTED = 'notice-withdrawal-submitted',
  MINIMUM_DEPOSIT_NOT_RECEIVED_REMINDER = 'minimum-deposit-not-received-reminder',
  MISSED_THE_BOAT = 'missed-the-boat',
  DEPOSIT_RECEIVED_OUTSIDE_WINDOW = 'deposit-received-outside-window',
}

export enum GenericNotificationTypeEnum {
  SAVINGS_ACCOUNT_CONFIRMATION = 'savings-account-confirmation',
  SAVINGS_ACCOUNT_UNFUNDED_1ST_REMINDER = 'savings-account-unfunded-1st-reminder',
  SAVINGS_ACCOUNT_UNFUNDED_2ND_REMINDER = 'savings-account-unfunded-2nd-reminder',
  SAVINGS_ACCOUNT_UNFUNDED_3RD_REMINDER = 'savings-account-unfunded-3rd-reminder',
  SAVINGS_ACCOUNT_CLOSURE = 'savings-account-closure',
  SAVING_ACCOUNT_ANNUAL_STATEMENT_UPLOADED = 'saving-account-annual-statement-uploaded',
  SAVING_ACCOUNT_TAX_CERTIFICATE_UPLOADED = 'saving-account-tax-certificate-uploaded',
  TERM_DEPOSIT_MATURITY_1ST_REMINDER = 'term-deposit-maturity-1st-reminder',
  TERM_DEPOSIT_MATURITY_2ND_REMINDER = 'term-deposit-maturity-2nd-reminder',
  TERM_DEPOSIT_MATURITY_3RD_REMINDER = 'term-deposit-maturity-3rd-reminder',
}

export enum NotificationCardTypeEnum {
  TOP_UP = 'top-up',
  WITHDRAWAL = 'withdrawal',
  PURCHASE = 'purchase',
  CHARGEBACK = 'chargeback',
  REFUND = 'refund',
  TRANSACTION_STATUS_UPDATE = 'transaction-status-update',
}

export enum NotificationAlertTypeEnum {
  SELECTED_VALUE = 'selectValue',
  TRANSACTIONS_SUCCESS_AND_FAILURE_ALERTS = 'transactionsSuccessAndFailureAlerts',
  CONVERSION_SUCCESS_AND_FAILURE_ALERTS = 'conversionSuccessAndFailureAlerts',
  DAILY_BALANCE = 'dailyBalance',
  PAYMENTS_REQUEST_ALERTS = 'paymentsRequestAlerts',
  ADD_BENEFICIARY_ALERTS = 'addBeneficiaryAlerts',
  DIRECT_DEBIT_MANDATE_ALERTS = 'directDebitMandateAlerts',
}

export enum NotificationTemplateEnum {
  OUTBOUND_NOTIFICATION = 'outbound-notification',
  INBOUND_NOTIFICATION = 'inbound-notification',
  DAILY_BALANCE = 'daily-balance',
  WELCOME_EMAIL = 'welcome-email',
  WELCOME_EMAIL_AFTER_KYC = 'welcome-email-after-kyc',
  ACCOUNT_CLOSURE = 'account-closure',
  PAYMENT_REJECTED = 'payment-rejected',
  DAILY_BALANCE_SUMMARY = 'daily-balance-summary',
  VERIFICATION_LINK = 'verification-link',
  VERIFICATION_CODE = 'verification-code',
  EXCHANGE_BOOKED = 'exchange-booked',
  EXCHANGE_CANCELLED = 'exchange-cancelled',
  EXCHANGE_COMPLETED = 'exchange-completed',
  EXCHANGE_FAILED = 'exchange-failed',
  EXCHANGE_PENDING = 'exchange-pending',
  PASSWORD_RESET_EMAIL = 'password-reset-email',
  PASSWORD_CHANGED_EMAIL = 'password-changed-email',
  ADD_BENEFICIARY = 'add-beneficiary',
  UPDATE_BENEFICIARY = 'update-beneficiary',
  REMOVE_BENEFICIARY = 'remove-beneficiary',
  SERVICE_PROVIDER_REMINDER = 'service-provider-reminder',
  GUEST_INVITE = 'guest-invite',
  DASHBOARD_PRIMARY_CONTACT_INVITE = 'dashboard-primary-contact-invite',
  DASHBOARD_TEAM_MEMBER_INVITE = 'dashboard-team-member-invite',
  ACCESS_PERSONA_GRANTED = 'access-persona-granted',
  SEND_EMAIL_CREDENTIALS = 'send-email-credentials',
  PAYMENT_REQUEST = 'payment-request',
  PAYMENT_REQUEST_STATUS = 'payment-request-status',
  THREEDS_CHALLENGE = '3ds-challenge',
  CARD_CREATED = 'card-created',
  PHYSICAL_CARD_ACTIVATED = 'physical-card-activated',
  DIGITAL_WALLET_AVAILABLE = 'digital-wallet-available',
  DIGITAL_WALLET_ACTIVATED = 'digital-wallet-activated',
  DIGITAL_WALLET_ABANDONED_PROVISIONING = 'digital-wallet-abandoned-provisioning',
  DIGITAL_WALLET_PAYMENT_REMINDER = 'digital-wallet-payment-reminder',
  DIGITAL_WALLET_PROVISIONING_OTP = 'digital-wallet-provisioning-otp',
  KYC_RFI_ACTION_REQUIRED = 'kyc-rfi-action-required',
  BULK_PAYMENT_REQUEST = 'bulk-payment-request',
  BULK_PAYMENT_REQUEST_APPROVAL = 'bulk-payment-request-approval',
  INDIVIDUAL_PROFILE_UPDATE = 'individual-profile-update',
  BUSINESS_PROFILE_UPDATE = 'business-profile-update',
  APPLICATION_REJECTION = 'application-rejection',
  TRANSACTION_NOTIFICATION = 'transaction-notification',
  KYC_APPLICATION_ACCEPTED = 'kyc-application-accepted',
  GENERIC_WORKFLOW = 'generic-workflow',
  FAILURE_ALERT = 'failure-alert',
  INCOMPLETE_ONBOARDING = 'incomplete-onboarding',
  CUSTOMER_DOCUMENT_UPLOAD_BACK_OFFICE_NOTIFICATION = 'customer-document-upload-back-office-notification',
  // Customer-facing confirmation sent to the uploader when their document is received
  CUSTOMER_DOCUMENT_UPLOAD_CONFIRMATION = 'customer-document-upload-confirmation',

  MESSAGE_RECEIVED = 'message-received',

  // Sent when account is created
  ACCOUNT_CREATED = 'account-created',

  // In savings context when account is opened but not yet funded send this reminder to fund the account and start earning interest
  NO_DEPOSIT_RECEIVED_REMINDER = 'no-deposit-received-reminder',

  // In savings context when account has been part-funded but is still below its minimum balance send this reminder before the funding window closes
  MINIMUM_DEPOSIT_NOT_RECEIVED_REMINDER = 'minimum-deposit-not-received-reminder',

  // In savings context when the funding window closes with the account still below its minimum balance (no deposit, or less than the minimum deposited)
  MISSED_THE_BOAT = 'missed-the-boat',

  // In savings context when a deposit arrives on an account that can no longer be funded (e.g. a fixed term after its funding window closed), so it is returned
  DEPOSIT_RECEIVED_OUTSIDE_WINDOW = 'deposit-received-outside-window',

  // For statements and documents when these are ready and uploaded to the platform for the customer to view/download
  STATEMENT_GENERATED = 'statement-generated',

  // When nominated account is changed for a customer send this notification to inform the customer of the change and potential actions they can take if they did not authorise this change
  NOMINATED_ACCOUNT_CHANGED = 'nominated-account-changed',

  // For FSCS when customer is approaching the balance limit and needs to be notified of the potential risk and actions they can take (this is across all accounts not just one)
  FSCS_BALANCE_LIMIT_APPROACHING = 'fscs-balance-limit-approaching',

  // For Retail and business when a third party user is added to the account send this notification to inform the customer of the change and potential actions they can take if they did not authorise this change
  THIRD_PARTY_ADDED = 'third-party-added',
  // In business context when a new director is added to the company
  DIRECTOR_CHANGE = 'director-change',

  MATURITY_WINDOW_STARTED = 'maturity-window-started',
  MATURITY_REMINDER = 'maturity-reminder',
  // For fixed term accounts when maturity date is approaching send this reminder to notify customer account has matured.
  ACCOUNT_MATURED = 'account-matured',
  // to be sent if fixed term auto rolls over into easy access and you could reinvest these funds into another fixed term
  DEFAULT_MATURITY_REINVEST_REMINDER = 'default-maturity-reinvest-reminder',
  // In savings context when notice or easy access rate changes and customer needs to be notified of the change and potential actions they can take
  RATE_CHANGE_NOTICE = 'rate-change-notice',
  // In savings context when notice or easy access rate changes and customer needs to be notified in advance of the change and potential actions they can take
  RATE_CHANGE_ADVANCE_NOTICE_X_DAYS = 'rate-change-advance-notice-x-days',
  // For notice accounts when customer was requested a withdrawal send this reminder of the approaching withdrawal date and potential actions they can take
  NOTICE_WITHDRAWAL_X_DAYS_REMAINING = 'notice-withdrawal-x-days-remaining',
  // For notice accounts, sent once when the customer submits a notice withdrawal request
  NOTICE_WITHDRAWAL_SUBMITTED = 'notice-withdrawal-submitted',

  BACKOFFICE_NEW_CHAT_ASSIGNED_GROUP = 'backoffice-new-chat-assigned-group',
  BACKOFFICE_NEW_CHAT_RESPONSE = 'backoffice-new-chat-response',
  BACKOFFICE_ADDED_TO_CHAT = 'backoffice-added-to-chat',
  ORGANISATION_MEMBER_INVITE = 'organisation-member-invite',
  // Fired to back-office agents who have opted in to a managed-failure category
  // (e.g. onboarding failures). Off by default — subscription is explicit.
  BACKOFFICE_MANAGED_FAILURE = 'backoffice-managed-failure',
  JOINT_ACCOUNT_INVITATION = 'joint-account-invitation',
  JOINT_ACCOUNT_INVITATION_ACTION = 'joint-account-invitation-action',
  JOINT_ACCOUNT_INVITATION_EXPIRED = 'joint-account-invitation-expired',
  DEVICE_ENROLLED = 'device-enrolled',
  DEVICE_REMOVED = 'device-removed',
  CREDITS_LOW_BALANCE = 'credits-low-balance',
  CREDITS_MEMBER_LIMIT = 'credits-member-limit',
  // Sent once a VRP mandate is fulfilled (active) with the payer's bank - GC's 1.1 setup
  // confirmation requirement (9920).
  VRP_MANDATE_SETUP_CONFIRMATION = 'vrp-mandate-setup-confirmation',
  // Sent ~48h before a scheduled VRP collection is charged - GC's 1.3 advance notice
  // requirement (9920).
  VRP_MANDATE_ADVANCE_NOTICE = 'vrp-mandate-advance-notice',
  // Proactive AI companion nudge (docs/platform/ai-companion/internal-api.md, "Proactive nudges").
  COMPANION_NUDGE = 'companion-nudge',
  // A Direct Debit mandate on the customer's account (they are the payer) changed, from the
  // provider's mandate webhooks (Keel Money only so far).
  DIRECT_DEBIT_MANDATE_CREATED = 'direct-debit-mandate-created',
  DIRECT_DEBIT_MANDATE_CANCELLED = 'direct-debit-mandate-cancelled',
  DIRECT_DEBIT_MANDATE_RETURNED = 'direct-debit-mandate-returned',
  DIRECT_DEBIT_MANDATE_CREATION_FAILED = 'direct-debit-mandate-creation-failed',
  // Project-wide "News" broadcasts from the admin App SDK (`notifications.broadcast.send`): in-app, push and email.
  NEWS_ANNOUNCEMENT = 'news-announcement',
}

export const transactionStatusMapping: Record<string, string> = {
  [NotificationTypeEnum.TRANSACTION_INCOMING]: 'incoming',
  [NotificationTypeEnum.TRANSACTION_OUTGOING]: 'outgoing',
};
export type NotificationPayload = {
  [key: string]:
    | string
    | NotificationTypeEnum
    | NotificationTransactionStatus
    | boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any;
};

export enum DefaultToggleOption {
  ON = 'on',
  OFF = 'off',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PushPayload = { [key: string]: any };

export type PublishNotificationWorkflowConfig = {
  name: NotificationTemplateEnum;
  identifier: string;
  version: number;
  is_sms_enabled: boolean;
  is_email_enabled: boolean;
  is_in_app_enabled: boolean;
  is_push_enabled: boolean;
  is_active: boolean;
  // Novu tenant key (e.g. 'gfc'), resolved against <KEY>_NOVU_TENANT in the target
  // deployment; omitted means the default NOVU_TENANT
  tenant?: string;
};

export type NotificationPayloadDataResponse = {
  id: string;
  payload: NotificationPayload;
};
